<template>
    <VJoyWrapper
        direction="row"
        column-gap="4"
        row-gap="3"
        :no-margin="true"
        align="flex-start"
        justify="flex-start"
        class="appraisal-card"
        :class="{'appraisal-card--hidden': (appraisal.hidden && isEditMode) || (appraisal.hiddenByClient && isClientAppraisal)}"
    >
        <VJoyAvatar
            class="appraisal-card__avatar"
            :full-name="appraisal.clientName"
            :photo-url="appraisal.clientImageUrl"
            :is-empty="false"
            :link="false"
            profile="default"
            show-letters
        />

        <VJoyWrapper direction="column" row-gap="1" :no-margin="true" align="flex-start" class="appraisal-card__content">
            <VJoyText preset="primary-450" tag-name="h4" class="appraisal-card__name">
                {{ appraisal.clientName }}
            </VJoyText>

            <VJoyWrapper
                v-if="appraisal.clientJobTitle || appraisal.clientCompanyName"
                direction="row"
                row-gap="0"
                column-gap="2"
                :no-margin="true"
                align="center"
            >
                <VJoyText v-if="appraisal.clientJobTitle" preset="primary-300" tag-name="span">
                    {{ appraisal.clientJobTitle }}
                </VJoyText>
                <VJoyText
                    v-if="appraisal.clientJobTitle && appraisal.clientCompanyName"
                    preset="primary-300"
                    tag-name="span"
                    class="appraisal-card__separator"
                >
                    -
                </VJoyText>
                <template v-if="appraisal.clientCompanyName">
                    <VJoyIcon :name="'company-placeholder' as JoyIconsId" size="small" />
                    <VJoyText preset="primary-300" tag-name="span">
                        {{ appraisal.clientCompanyName }}
                    </VJoyText>
                </template>
            </VJoyWrapper>

            <VJoyRatingStars class="appraisal-card__rating" :rating-value="appraisal.generalRating" size="medium" />
            <VJoyWrapper direction="row" row-gap="0" column-gap="2" :no-margin="true">
                <VJoyText v-if="projectLengthLabel" preset="primary-300" tag-name="p" class="appraisal-card__date">
                    {{ projectLengthLabel }}
                </VJoyText>

                <VJoyText v-if="projectLengthLabel" preset="primary-300">-</VJoyText>

                <VJoyText preset="primary-300" tag-name="p" class="appraisal-card__date">
                    {{ t('profile.resume.appraisals.reviewed.on', [formattedAppraisalDate]) }}
                </VJoyText>
            </VJoyWrapper>

            <VJoyText preset="primary-300" tag-name="span" class="appraisal-card__comment">
                <SeeMoreOrRedirect
                    v-if="appraisal.comment"
                    :max-lines="4"
                    :locked="{
                        allowHref: !mf,
                        label: t('profile.section.appraisals.private', [profile?.firstname]),
                        componentName: 'profile-appraisals-section',
                        targetId: anchorsPageProfile.appraisalsSection,
                    }"
                    @see-more-clicked-when-locked="$emit('seeMoreClickedWhenLocked')"
                >
                    {{ appraisal.comment }}
                </SeeMoreOrRedirect>
            </VJoyText>
        </VJoyWrapper>

        <VJoyWrapper
            v-if="isEditMode || isClientAppraisal"
            direction="row"
            justify="flex-end"
            align="center"
            :no-margin="true"
            class="appraisal-card__visibility-container"
        >
            <VJoyButton
                variant="ghost"
                size="small"
                :icon="(isHidden ? 'eye' : 'eye-masked') as JoyIconsId"
                :label="isHidden ? t('profile.section.recommendation.show.label') : t('profile.section.recommendation.hide.label')"
                icon-position="left"
                class="appraisal-card__visibility-button"
                :disabled="isUpdatingAppraisalVisibility"
                :loading="isUpdatingAppraisalVisibility"
                @click="handleToggleVisibility"
            />
        </VJoyWrapper>
    </VJoyWrapper>
</template>

<script setup lang="ts">
    import {differenceInDays} from 'date-fns';
    import {storeToRefs} from 'pinia';
    import {computed, ref, useAuth, useFormat, useTranslation} from '#imports';
    import {VJoyAvatar, VJoyButton, VJoyIcon, VJoyRatingStars, VJoyText, VJoyWrapper} from '@maltjoy/core-vue';
    import type {JoyIconsId} from '@maltjoy/icons';
    import SeeMoreOrRedirect from '~/components/_common/see-more/SeeMoreOrRedirect.vue';
    import {anchorsPageProfile} from '~/config/anchors';
    import {useAppraisalStore} from '~/features/appraisals/store/appraisalStore';
    import {useProfileStore} from '~/store/profileStore';
    import type {Appraisal} from '~/types/domain/Appraisals';

    const {t} = useTranslation();
    const {formatDateToShort} = useFormat();
    const {updateAppraisalVisibility} = useAppraisalStore();
    const {profile} = storeToRefs(useProfileStore());
    const {user} = storeToRefs(useAuth());
    const isUpdatingAppraisalVisibility = ref<boolean>(false);

    type Props = {
        appraisal: Appraisal;
        isEditMode: boolean;
        mf: boolean;
    };
    const props = defineProps<Props>();

    const emits = defineEmits(['seeMoreClickedWhenLocked']);

    const isClientAppraisal = computed(() => !!props.appraisal.clientAccountId && props.appraisal.clientAccountId === user.value?.id);
    const isHidden = computed(() => (isClientAppraisal.value ? props.appraisal.hiddenByClient : props.appraisal.hidden));
    const formattedAppraisalDate = computed(() => formatDateToShort(props.appraisal.creationDate));

    const projectLengthLabel = computed(() => {
        if (!props.appraisal.startDate || !props.appraisal.endDate) return null;
        const diffDays = differenceInDays(new Date(props.appraisal.endDate), new Date(props.appraisal.startDate));
        if (diffDays < 14) return t('profile.resume.appraisals.project.length.days');
        if (diffDays < 60) return t('profile.resume.appraisals.project.length.weeks');
        if (diffDays < 30 * 6) return t('profile.resume.appraisals.project.length.months');
        if (diffDays < 365) return t('profile.resume.appraisals.project.length.less.than.one.year');
        if (diffDays < 730) return t('profile.resume.appraisals.project.length.more.than.one.year');
        return t('profile.resume.appraisals.project.length.years', {years: Math.floor(diffDays / 365)});
    });

    async function handleToggleVisibility() {
        isUpdatingAppraisalVisibility.value = true;
        await updateAppraisalVisibility(props.appraisal);
        isUpdatingAppraisalVisibility.value = false;
    }
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint' as joy;

    .appraisal-card {
        position: relative;
        flex-wrap: wrap;
        width: 100%;

        &--hidden {
            .appraisal-card__avatar,
            .appraisal-card__content {
                opacity: 0.5;
            }
        }

        @include joy.screen_sm(min) {
            flex-wrap: nowrap;
        }

        &__avatar {
            flex-shrink: 0;
        }

        &__content {
            flex: 1;
            width: 100%;
            min-width: 160px;
        }

        &__name {
            margin: 0;
        }

        &__separator {
            color: var(--joy-color-neutral-40);
        }

        &__date {
            color: var(--joy-color-neutral-40);
        }

        &__visibility-container {
            width: 100%;
            justify-content: center;
            align-self: center;

            @include joy.screen_sm(min) {
                width: auto;
                margin-left: auto;
                justify-content: flex-end !important;
            }
        }
    }
</style>
